import React, { useState, useEffect } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Manager, Reference, Popper } from 'react-popper'

import { colors } from '../../variables.style'
import { Button } from '../../common/components/button'
import { Modal } from '../../common/components/modal'
import { ExplicitChangeTextInput } from '../../common/components/explicit-change-text-input'

import { useStyles, calculateLabelColumnWidth } from './checkbox-grid.style'

export const CheckboxGrid = function ({
  label,
  labelSize,
  rows = [],
  onRowsChange = () => {},
  columns = [],
  selections = [],
  onSelectionChange = () => {}
}) {
  const classes = useStyles()

  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    ensureMaxSelections(selections)
  }, [rows, selections])

  function isComboAllowed (allowMulti, currentValues, newValue) {
    if (allowMulti === false && currentValues.length > 0) {
      return false
    }
    if (Array.isArray(allowMulti) && currentValues.length > 0) {
      const hasDisallowedValue = !!currentValues.find((v) => !allowMulti.includes(v))
      return !hasDisallowedValue && allowMulti.includes(newValue)
    }
    return true
  }

  function handleSelectionChange (row, column, isChecked) {
    let newSelections = [ ...selections ]
    if (isChecked) {
      const rowConfig = rows.find(({ value }) => row === value)
      const selectedColumns = newSelections.filter((v) => v.row === row).map((v) => v.column)
      if (!isComboAllowed(rowConfig.allowMulti, selectedColumns, column)) {
        // if new selection results in a combo that isn't allowed - clear all before selecting the new one
        newSelections = removeChecked(newSelections, { row })
      }
      const columnConfig = columns.find(({ value }) => column === value)
      const selectedRows = newSelections.filter((v) => v.column === column).map((v) => v.row)
      if (!isComboAllowed(columnConfig.allowMulti, selectedRows, row)) {
        // if new selection results in a combo that isn't allowed - clear all before selecting the new one
        newSelections = removeChecked(newSelections, { column })
      }
      newSelections = [...newSelections, { row, column }]
    } else {
      newSelections = removeChecked(newSelections, { row, column })
    }
    onSelectionChange(newSelections)
  }

  function ensureMaxSelections (selections) {
    columns.forEach(({ value, maxSelectionCount, maxSelectionMessage }) => {
      if (!maxSelectionCount) {
        return
      }

      const selectedSubgroups = selections.filter(({ column }) => (column === value))
      if (selectedSubgroups.length >= maxSelectionCount) {
        rows.forEach((row) => {
          if (findChecked(selections, { row: row.value, column: value })) {
            return
          }

          if (row.disabledColumns) {
            if (!row.disabledColumns.includes(value)) {
              row.disabledColumns = [...row.disabledColumns, value]
            }
          } else {
            row.disabledColumns = [value]
          }

          row.disabledColumnMessage = {
            ...row.disabledColumnMessage,
            [value]: maxSelectionMessage
          }
        })
      }
    })
  }

  function isCurrentlyChecked (row, column) {
    return !!findChecked(selections, { row, column })
  }

  function isMatch (selected, { row, column }) {
    return (row === undefined || row === selected.row) && (column === undefined || column === selected.column)
  }

  function findChecked (selected, { row, column }) {
    return selected.find((s) => isMatch(s, { row, column }))
  }

  function removeChecked (selected, { row, column }) {
    return selected.filter((s) => !isMatch(s, { row, column }))
  }

  function getCellClass ({ r: row = {}, c: column = {} }) {
    if (row.isError || column.isError) {
      return classes.error
    }
    if (row.isWarning || column.isWarning) {
      return classes.warning
    }
    return ''
  }

  function getKey (value) {
    if (value === null) {
      return 'null'
    }
    return value.toString()
  }

  function handleDragEnd (draggable) {
    const { source, destination } = draggable

    if (!destination) {
      return
    }

    const updatedRows = [ ...rows ]
    swapRows(updatedRows, source.index, destination.index)

    onRowsChange(updatedRows)
  }

  function swapRows (rows, sourceIndex, destinationIndex) {
    const [removedItem] = rows.splice(sourceIndex, 1)
    rows.splice(destinationIndex, 0, removedItem)
  }

  function handleRowLabelChange (value, label) {
    const updatedRows = rows.map((r) => {
      if (r.value === value) {
        r = { ...r, label }
      }
      return r
    })
    onRowsChange(updatedRows)
  }

  function handleModalClose () {
    setIsModalOpen(false)
  }

  return (
    <>
    <div className={classes.container}>
      <div className="flex-row">
        <div className={classes.grabberCell} />
        {/* Column label row does not have a border, so it needs 1 less pixel */}
        <div className={classes.gridLabel} style={{ width: calculateLabelColumnWidth(columns.length) - 1 }}>
          <div className={(labelSize === 'medium') ? 'form-element-label-medium' : 'form-element-label'}>{label}</div>
          <Button variant="link" className={classes.editLabelsButton} onClick={() => { setIsModalOpen(true) }}>Edit Labels</Button>
        </div>
        {columns.map((c) => (
          <div key={c.value} className={`${classes.columnLabelCell} ${getCellClass({ c })}`}>{c.label}{c.isRequired && '*'}</div>
        ))}
      </div>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div data-qa="subgroup-values">
          <Droppable droppableId="subgroup-values">
            {(provided, snapshot) => (
              <div className={classes.rows} ref={provided.innerRef} style={{ backgroundColor: snapshot.isDraggingOver ? colors.metallicSeaweed : 'transparent' }} {...provided.droppableProps}>
                {rows.map((r, index) => (
                  <Draggable key={getKey(r.value)} draggableId={getKey(r.value)} index={index}>
                    {(provided) => (
                      <div data-qa={`checkbox-grid-row-${getKey(r.value)}`} className={classes.gridRow} ref={provided.innerRef} {...provided.draggableProps}>
                        <div className={`${classes.grabberCell} ${getCellClass({ r })}`}><div className="font-icon-grabber" {...provided.dragHandleProps} tabIndex={-1}/></div>
                        <div className={`${classes.labelCell} ${getCellClass({ r })}`} style={{ width: calculateLabelColumnWidth(columns.length) }}>
                          {r.label}{r.isRequired && '*'} {r.label !== r.value && r.value !== null && `(${r.value})`}
                        </div>
                        {columns.map((c) => (
                          <div key={c.value} className={`${classes.checkCell} ${getCellClass({ r, c })}`}>
                            <Checkbox
                              row={r}
                              column={c}
                              isChecked={isCurrentlyChecked(r.value, c.value)}
                              onChange={(event) => {
                                handleSelectionChange(r.value, c.value, event.target.checked)
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
    <Modal bodyClassName={classes.modalBody} isOpen={isModalOpen} onClose={handleModalClose} title="Edit Subgroup Labels"
      footer={<Button variant="secondary" onClick={handleModalClose}>Done</Button>} enableOverlayClick={false}>
        <>
        {rows.map(({ value, label }) => (
          <div key={value} className={classes.labelEditRow}>
            <div className={classes.labelEditRowValue}>{value === null ? label : value} </div>
            <div className={classes.labelEditRowLabel}>
              <ExplicitChangeTextInput size="medium" value={label} onChange={(event) => {
                handleRowLabelChange(value, event.target.value)
              }} />
            </div>
          </div>
        ))}
        </>
    </Modal>
    </>
  )
}

function Checkbox ({ row, column, isChecked, onChange }) {
  const classes = useStyles()

  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const isDisabled = row.disabledColumns && row.disabledColumns.includes(column.value)
  const disabledColumnMessage = isDisabled && row.disabledColumnMessage && row.disabledColumnMessage[column.value]

  function showTooltip () {
    setIsTooltipOpen(true)
  }

  function hideTooltip () {
    setIsTooltipOpen(false)
  }

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div className={classes.checkWrapper} >
            <input
              ref={ref}
              type="checkbox"
              name={`${row.value}_${column.value}`}
              data-qa={`${row.value}_${column.value}`}
              checked={isChecked}
              disabled={isDisabled}
              onChange={onChange}
            />
            {isDisabled && <div className={classes.disabledCheckCatcher} onMouseOver={showTooltip} onMouseOut={hideTooltip} />}
          </div>
        )}
      </Reference>
      {disabledColumnMessage && isTooltipOpen &&
        <Popper placement="top">
          {({ ref, style, placement, arrowProps }) => (
            <div ref={ref} style={style} data-placement={placement} className={classes.tooltipContent}>
              {disabledColumnMessage}
              <div ref={arrowProps.ref} style={arrowProps.style} className="tooltip-tip" />
            </div>
          )}
        </Popper>
      }
    </Manager>
  )
}
